import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import request from './request';
import { DATA_QUERY_EXPIRATION, QUERY_RETRY } from './queries';

export type ModelStatus = 'not_started' | 'running' | 'fail' | 'success';

export type ModelType = 'mm' | 'da' | 'lt';

export type ModelListItem = {
  id: string;
  modelName: string;
  status: ModelStatus;
  createdAt: string;
  kpiMetric: string | null;
  /** Not returned by the models-list endpoint — retired concept (see kpiUnit) */
  kpiCategory?: string | null;
  kpiUnit?: 'euro' | 'unit' | null;
  mediaPlatforms: string[];
  campaignCount: number;
  quickRun: boolean;
  /** True when a published model_version exists — i.e. this model's results
   * are what the navbar dropdown shows. Optional until every environment
   * serves the field. */
  hasPublishedVersion?: boolean;
  /** Lineage generation counter: how many versions have been built for this
   * model (each rebuild adds one). 0 = no version uploaded yet. Optional
   * until every environment serves the field. */
  versionNumber?: number;
  /** When the next automated rebuild is due (ISO 8601 UTC). Null when the
   * model has no automated published version. Optional until every
   * environment serves the field. */
  nextRunAt?: string | null;
  /** Which service the model belongs to — lets the UI route the user to the
   * matching dashboard. Optional until every environment serves the field. */
  modelType?: ModelType;
  /** For long-term models: id of the parent Marketing Mix model. Null for
   * mm/da models. Optional until every environment serves the field. */
  parentModelId?: string | null;
};

export type ModelCampaign = {
  campaignId: string;
  campaignName: string | null;
  blacklist: boolean;
  groupId: string | null;
  groupName: string | null;
};

export type ModelDetails = {
  id: string;
  modelName: string;
  status: ModelStatus;
  createdAt: string;
  kpiSource: string | null;
  kpiMetric: string | null;
  kpiCategory: string | null;
  kpiUnit?: 'euro' | 'unit' | null;
  mediaPlatforms: string[];
  campaigns: ModelCampaign[];
  modelType: ModelType;
  parentModelId?: string | null;
};

type ModelsListResponse = {
  data: ModelListItem[];
};

type ModelDetailsResponse = {
  data: ModelDetails;
};

export const modelsQueryKey = (customer: string) =>
  ['useModelsQuery', customer] as const;

const modelDetailsQueryKey = (customer: string, modelId: string) =>
  ['useModelDetailsQuery', customer, modelId] as const;

export function useModelsQuery(customer: string) {
  const queryId = useMemo(() => modelsQueryKey(customer), [customer]);

  return useQuery(
    queryId,
    async () => {
      const response = await request.get<ModelsListResponse>(
        '/data/models-list',
        { params: { customer } },
      );
      return response.data?.data ?? [];
    },
    {
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
      enabled: Boolean(customer),
    },
  );
}

export function useModelDetailsQuery(customer: string, modelId: string | null) {
  const queryId = useMemo(
    () => modelDetailsQueryKey(customer, modelId ?? ''),
    [customer, modelId],
  );

  return useQuery(
    queryId,
    async () => {
      if (!modelId) return null;
      const response = await request.get<ModelDetailsResponse>('/data/model', {
        params: { customer, model_id: modelId },
      });
      return response.data?.data ?? null;
    },
    {
      retry: QUERY_RETRY,
      staleTime: DATA_QUERY_EXPIRATION,
      enabled: Boolean(customer) && Boolean(modelId),
    },
  );
}

type UpdateModelVariables = { modelId: string } & CreateModelInput;

type DeleteModelVariables = {
  modelId: string;
};

export function useUpdateModelMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ modelId, ...data }: UpdateModelVariables) => {
      const response = await request.put<ModelDetailsResponse>(
        '/data/model',
        data,
        { params: { customer, model_id: modelId } },
      );
      return response.data?.data;
    },
    {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries(modelsQueryKey(customer));
        queryClient.invalidateQueries(
          modelDetailsQueryKey(customer, variables.modelId),
        );
        // Re-running a model demotes its published versions immediately —
        // refetch the navbar dropdown data (all model types) so they don't
        // linger until a page refresh.
        queryClient.invalidateQueries(['useModelVersionsQuery', customer]);
      },
    },
  );
}

export function useDeleteModelMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ modelId }: DeleteModelVariables) => {
      const response = await request.delete<{ data: boolean }>('/data/model', {
        params: { customer, model_id: modelId },
      });
      return response.data?.data ?? false;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(modelsQueryKey(customer));
        // Deleting a pair removes its versions — refetch the navbar
        // dropdown data (all model types) so the deleted model disappears
        // without a page refresh.
        queryClient.invalidateQueries(['useModelVersionsQuery', customer]);
      },
    },
  );
}

/** Standard (mm/da) model payload for the Settings create/update endpoints. */
export type CreateModelStdInput = {
  modelName: string;
  kpiSource: string;
  kpiMetric: string;
  kpiCategory: string | null;
  kpiUnit: 'euro' | 'unit';
  selectedMedia: string[];
  campaignSelections: Record<string, boolean>;
  modelType: 'mm' | 'da';
};

/** Long-term model payload: KPI/media/campaigns are inherited server-side
 * from the parent MM model, so only the name and parent reference travel. */
export type CreateModelLtInput = {
  modelName: string;
  modelType: 'lt';
  parentModelId: string;
};

export type CreateModelInput = CreateModelStdInput | CreateModelLtInput;

type CreateModelVariables = {
  models: CreateModelInput[];
};

type CreateModelResponse = {
  success: boolean;
  models: Array<{
    userModelName: string;
    quick: {
      modelId: string;
      modelName: string;
      jobId: string;
      jobName: string;
    };
    full: {
      modelId: string;
      modelName: string;
      jobId: string;
      jobName: string;
    };
  }>;
  totalModels: number;
  message: string;
};

export function useCreateModelMutation(customer: string) {
  const queryClient = useQueryClient();

  return useMutation(
    async (variables: CreateModelVariables) => {
      const response = await request.post<CreateModelResponse>(
        '/data/model',
        variables,
        { params: { customer } },
      );
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(modelsQueryKey(customer));
      },
    },
  );
}

export type ModelStatusResponse = {
  modelId: string;
  status: ModelStatus;
  startTime: string | null;
  lastUpdate: string | null;
  progress: number;
  iterationsDone: number;
  iterationsTotal: number;
  estimatedTimeRemaining: number | null;
  errorMessage: string | null;
};

export function useModelStatusQuery(
  customer: string,
  modelId: string | null,
  refetchInterval: number | false = 5000,
) {
  return useQuery(
    ['modelStatus', customer, modelId],
    async () => {
      const response = await request.get<{ data: ModelStatusResponse }>(
        '/data/models',
        { params: { customer, modelId } },
      );
      return response.data?.data ?? null;
    },
    {
      enabled: Boolean(customer) && Boolean(modelId),
      refetchInterval,
      staleTime: 0,
    },
  );
}

export function useModelsListPolling(
  customer: string,
  refetchInterval: number | false = 5000,
) {
  return useQuery(
    ['modelsListPolling', customer],
    async () => {
      const response = await request.get<ModelsListResponse>(
        '/data/models-list',
        { params: { customer } },
      );
      return response.data?.data ?? [];
    },
    {
      enabled: Boolean(customer),
      refetchInterval,
      staleTime: 0,
    },
  );
}
